import { CalendarPlus, TriangleAlert } from "lucide-react";

import { EmployeeAvatar } from "@/components/timeclock/EmployeeAvatar";
import { StatusChip } from "@/components/timeclock/StatusChip";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  formatBalance,
  formatClock,
  formatDuration,
  formatStamp,
  formatWorkDate,
  nextActions,
  statusOf,
  workedMinutes,
} from "#shared/timeclock";
import type { Employee, EmployeeBalance, PunchAction, Shift } from "#shared/types";
import { vacationDaysLeft } from "#shared/vacation";

/** Halva dagar förekommer, men "12,5 dagar" ska inte skrivas som "12,50". */
function formatDays(days: number): string {
  return `${days.toLocaleString("sv-SE", { maximumFractionDigits: 1 })} ${days === 1 ? "dag" : "dagar"}`;
}

const ACTION_LABELS: Record<PunchAction, string> = {
  check_in: "Stämpla in",
  lunch_start: "Börja lunch",
  lunch_end: "Avsluta lunch",
  check_out: "Stämpla ut",
};

const ACTION_STYLES: Record<PunchAction, string> = {
  check_in: "bg-success text-success-foreground",
  lunch_start: "bg-warning text-warning-foreground",
  lunch_end: "bg-success text-success-foreground",
  check_out: "bg-destructive text-destructive-foreground",
};

export function PunchDialog({
  employee,
  shift,
  missedShift,
  balance,
  pending,
  onClose,
  onPunch,
  onRequestLeave,
}: {
  employee: Employee | null;
  shift?: Shift;
  /** Ett tidigare pass som aldrig stämplades ut. */
  missedShift?: Shift;
  balance?: EmployeeBalance;
  pending: boolean;
  onClose: () => void;
  onPunch: (action: PunchAction) => void;
  onRequestLeave: () => void;
}) {
  const actions = nextActions(shift);
  const status = statusOf(shift);
  const vacationLeft = balance ? vacationDaysLeft(balance) : null;

  return (
    <Dialog open={!!employee} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md rounded-3xl p-7">
        {employee && (
          <>
            <div className="flex flex-col items-center">
              <EmployeeAvatar
                name={employee.name}
                photoUrl={employee.photo_url}
                className="size-28 text-7xl"
              />
              <DialogTitle className="mt-4 text-2xl font-semibold">{employee.name}</DialogTitle>
              <DialogDescription asChild>
                <div className="mt-2">
                  <StatusChip status={status} />
                </div>
              </DialogDescription>
            </div>

            {missedShift && (
              <div className="mt-5 flex gap-3 rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
                <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                <p>
                  Du stämplade in {formatWorkDate(missedShift.work_date)} kl.{" "}
                  {formatClock(missedShift.check_in)} men aldrig ut. Säg till en administratör
                  vilken tid du gick, så rättas dagen innan den skickas till lönen.
                </p>
              </div>
            )}

            <dl className="tabular mt-5 grid grid-cols-4 gap-2 rounded-2xl bg-muted p-4 text-center">
              {[
                ["In", shift?.check_in ?? null],
                ["Lunch", shift?.lunch_start ?? null],
                ["Tillbaka", shift?.lunch_end ?? null],
                ["Ut", shift?.check_out ?? null],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="text-[11px] font-medium text-muted-foreground uppercase">
                    {label}
                  </dt>
                  <dd className="mt-0.5 text-base font-semibold">{formatClock(value)}</dd>
                </div>
              ))}
            </dl>

            {shift?.check_in && (
              <p className="mt-3 text-center text-sm text-muted-foreground">
                Arbetat idag: <strong>{formatDuration(workedMinutes(shift))}</strong>
              </p>
            )}

            {balance && (balance.flex_hours != null || vacationLeft != null) && (
              // Tidpunkten står synlig: saldona räknas om av Fortnox vid lönekörning,
              // så de följer inte dagens stämplingar. En tooltip syns inte på en
              // pekskärm.
              <p className="mt-1 text-center text-sm text-muted-foreground">
                {balance.flex_hours != null && (
                  <>
                    Flexsaldo: <strong>{formatBalance(balance.flex_hours)}</strong>
                    {balance.comp_hours ? (
                      <> · komptid {formatBalance(balance.comp_hours)}</>
                    ) : null}
                  </>
                )}
                {vacationLeft != null && (
                  <span className={cn("block", balance.flex_hours != null && "mt-0.5")}>
                    Semester: <strong>{formatDays(vacationLeft)}</strong> kvar
                    {balance.vacation_saved_days
                      ? ` (varav ${balance.vacation_saved_days.toLocaleString("sv-SE", {
                          maximumFractionDigits: 1,
                        })} sparade)`
                      : null}
                  </span>
                )}
                <span className="mt-0.5 block text-xs">
                  enligt Fortnox {formatStamp(balance.fetched_at)}
                </span>
              </p>
            )}

            <div className="mt-5 flex flex-col gap-3">
              {actions.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground">
                  Dagen är avslutad. Be en administratör rätta tiderna om något blivit fel.
                </p>
              ) : (
                actions.map((action) => (
                  <button
                    key={action}
                    disabled={pending}
                    onClick={() => onPunch(action)}
                    className={cn(
                      "press h-16 rounded-2xl text-xl font-semibold disabled:opacity-60",
                      ACTION_STYLES[action],
                    )}
                  >
                    {ACTION_LABELS[action]}
                  </button>
                ))
              )}

              <button
                onClick={onRequestLeave}
                className="press flex h-14 items-center justify-center gap-2 rounded-2xl bg-secondary text-base font-medium text-secondary-foreground"
              >
                <CalendarPlus className="size-5" />
                Ansök om ledighet
              </button>

              <button
                onClick={onClose}
                className="press h-14 rounded-2xl text-lg font-medium text-muted-foreground"
              >
                Avbryt
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
